<template>
  <div class="pricing-component q-mt-lg q-pt-md q-pb-md">
    <h3 class="text-center">
      {{ isInterim ? t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_INTERIM_SECTION_TITLE") : $t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_AE_SECTION_TITLE") }}
    </h3>

    <QList class="text-left">
      <QItem>
        <QItemSection>
          <QItemLabel>
            <strong>{{ t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_DURATION") }}</strong>
          </QItemLabel>
          <QItemLabel caption>
            {{
              isInterim
                ? t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_NUMBER_OF_INTERIM", pricingBoost.getTotalExtra())
                : t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_NUMBER_OF_EXTRA", pricingBoost.getTotalExtra())
            }}
          </QItemLabel>
        </QItemSection>

        <QItemSection side>
          <QItemLabel>
            <strong>{{ pricingBoost.getDurationDisplay() }}</strong>
          </QItemLabel>
        </QItemSection>
      </QItem>

      <QItem>
        <QItemSection>
          <QItemLabel>
            <b>
              {{
                isInterim
                  ? t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_TOTAL_FACTURED_HOUR_INTERIM")
                  : t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_HT_PRICE_HOUR")
              }}
            </b>
          </QItemLabel>
          <QItemLabel caption>
            {{ pricingBoost.getQualification() }}
          </QItemLabel>
        </QItemSection>

        <QItemSection side>
          <QItemLabel>
            <!-- Only show boost pricing for non-interim contracts -->
            <small v-if="hasBoost && !isInterim" class="q-mr-sm">
              <s>{{ pricingBoost.getHourlyRateDisplay() }}</s>
            </small>
            <strong>{{ displayedHourlyRate }}</strong>
          </QItemLabel>
        </QItemSection>
      </QItem>

      <QSeparator spaced inset />

      <QItem class="text-secondary">
        <QItemSection avatar>
          <QIcon color="secondary" name="icon-f-file-text" />
        </QItemSection>
        <QItemSection>
          <QItemLabel>
            <strong>
              {{ isInterim ? t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_TOTAL_HT_INTERIM") : t("ETAB.PAGES.DEMANDS.CREATION.@PRICING_TOTAL_HT") }}
            </strong>
          </QItemLabel>
        </QItemSection>

        <QItemSection side>
          <QItemLabel class="text-secondary">
            <!-- Only show boost pricing for non-interim contracts -->
            <small v-if="hasBoost && !isInterim" class="q-mr-sm">
              <s>{{ pricingBoost.getPriceDisplay() }}</s>
            </small>
            <strong>{{ displayedTotalPrice }}</strong>
          </QItemLabel>
        </QItemSection>
      </QItem>
    </QList>
    <div v-if="isInterim" class="pricing-disclaimer q-mx-sm q-pa-sm">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p class="q-mb-none text-left" v-html="t('ETAB.PAGES.DEMANDS.CREATION.@PRICING_INTERIM_DISCLAIMER')"></p>
    </div>
  </div>
</template>

<script setup>
  import { computed } from "vue";
  import { useI18n } from "vue-i18n";
  import { NumericHelpers } from "@core/helpers";
  import { CreationStepPricingBoostModel } from "@core/models";

  const props = defineProps({
    pricingBoost: {
      type: Object,
      default: () => new CreationStepPricingBoostModel(),
    },
    boostPercentage: {
      type: Number,
      default: 0,
    },
    isInterim: {
      type: Boolean,
      default: false,
    },
  });

  const { t } = useI18n();

  const hasBoost = computed(() => props.boostPercentage > 0);

  const basePrice = computed(() => props.pricingBoost.getDuration() * props.pricingBoost.getHourlyRate());

  const boostedHourlyRate = computed(() => {
    if (props.isInterim) return props.pricingBoost.getHourlyRateDisplay();

    const boostedAmount = NumericHelpers.applyPercentage({
      amount: props.pricingBoost.getHourlyRate(),
      percentage: props.boostPercentage,
    });
    return NumericHelpers.formatNumberAsCurrency({
      number: boostedAmount,
      forceTrailingZeros: true,
    });
  });

  const boostedPrice = computed(() => {
    if (props.isInterim) return props.pricingBoost.getPriceDisplay();

    const boostedAmount = NumericHelpers.applyPercentage({
      amount: basePrice.value,
      percentage: props.boostPercentage,
    });
    return NumericHelpers.formatNumberAsCurrency({
      number: boostedAmount,
      forceTrailingZeros: true,
    });
  });

  const displayedHourlyRate = computed(() => (props.isInterim ? props.pricingBoost.getHourlyRateDisplay() : boostedHourlyRate.value));

  const displayedTotalPrice = computed(() => (props.isInterim ? props.pricingBoost.getPriceDisplay() : boostedPrice.value));
</script>

<style lang="scss">
  .pricing-component {
    border-radius: 4px;
    box-shadow: $card-shadow;
    background: white;

    .pricing-disclaimer {
      background: rgba(7, 44, 85, 0.05);
      border-radius: 4px;
      font-size: 12px;
      color: $primary;
      line-height: 1.4;
    }
  }
</style>
